import { ElementRef, Signal, signal } from '@angular/core';
import inspect from 'browser-util-inspect';
import { ThrottleSettings, camelCase, cloneDeep, isEqual, padStart, range, throttle } from 'lodash';
import {
    BehaviorSubject,
    Observable,
    Observer,
    OperatorFunction,
    Subject,
    Subscription,
    defer,
    filter,
    first,
    firstValueFrom,
    map,
    timeout,
} from 'rxjs';

export type PromiseResolve<T> = (value?: T | PromiseLike<T>) => void;
export type PromiseReject = (reason: any) => void;

export const arrayLikeEqual = (a: any, b: any): boolean => {
    if (!a || !b || a.length !== b.length) {
        return false;
    } else {
        return a.every((value, index) => value === b[index]);
    }
};

export const valueOr = <T>(value: T | undefined, or: T) => (value === undefined || value === null ? or : value);
export const numberValueOr = (value: any, or: number) => (typeof value !== 'number' || isNaN(value) ? or : value);
export const isTruthy = (value: any) => {
    if (typeof value === 'string') {
        const strValue = (value as string).toLowerCase();
        if (strValue === 'false' || strValue === 'f' || strValue === 'n' || strValue === 'no' || strValue === '0') {
            return false;
        }
    }
    return !!value;
};
export const equalOrBothNan = (a: number, b: number) => a === b || (isNaN(a) && isNaN(b));

export const paddedHex = (value: number, width: number, upperCase = true) => {
    const padded = padStart(value.toString(16), width, '0');
    return upperCase ? padded.toUpperCase() : padded.toLowerCase();
};

export const arrayToHex = (array: ArrayLike<number>, upperCase = true): string => {
    let out = '';

    for (let i = 0; i < array.length; ++i) {
        out += paddedHex(array[i], 2, upperCase);
    }
    return out;
};

export const hexToByteArray = (str: string): number[] => {
    const nBytes = Math.floor(str.length / 2);

    const out = new Array<number>(nBytes);
    for (let i = 0; i < nBytes; ++i) {
        out[i] = parseInt(str.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
};

export const coerceArrayLike = (v: string | ArrayLike<number>) => (typeof v === 'string' ? range(v.length).map((i) => v.charCodeAt(i)) : v);

export const arrayMin = (values: number[]) => (values.length > 1 ? values.reduce((a, b) => Math.min(a, b)) : Math.min(...values));
export const arrayMax = (values: number[]) => (values.length > 1 ? values.reduce((a, b) => Math.max(a, b)) : Math.max(...values));

// Below from npm's crc32 by T. Jameson Little - MIT license incorporated here by reference

export class Crc32 {
    private static table: Array<number> = (() => {
        const tab: number[] = [];
        const poly = 0xedb88320; // reverse polynomial
        for (let n = 0; n < 256; n += 1) {
            let c = n;
            for (let k = 0; k < 8; k += 1) {
                if (c & 1) {
                    c = poly ^ (c >>> 1);
                } else {
                    c = c >>> 1;
                }
            }
            tab[n] = c >>> 0;
        }
        return tab;
    })();

    private crc = 0 ^ -1;

    static calc(input: ArrayLike<number> | string): number {
        let crc = 0 ^ -1;
        const arr = coerceArrayLike(input);

        for (let i = 0; i < arr.length; i += 1) {
            crc = (crc >>> 8) ^ Crc32.table[(crc ^ arr[i]) & 0xff];
        }
        return (crc ^ -1) >>> 0;
    }

    append(input: ArrayLike<number> | string): number {
        const arr = coerceArrayLike(input);

        for (let i = 0; i < arr.length; i += 1) {
            this.crc = (this.crc >>> 8) ^ Crc32.table[(this.crc ^ arr[i]) & 0xff];
        }
        return (this.crc ^ -1) >>> 0;
    }

    get(): number {
        return (this.crc ^ -1) >>> 0;
    }

    reset() {
        this.crc = 0 ^ -1;
    }
}

interface PromiseTaskQueueEntry<T> {
    task: () => Promise<T>;
    resolve: (v: T) => void;
    reject: (e: any) => void;
}

interface PromiseTaskInnerQueue {
    tasks: Array<PromiseTaskQueueEntry<unknown>>;
    period: number;
    integrator: number;
}

export type PromiseTaskQueueKey = string | number | symbol;

/**
 * Run-to-completion task scheduler for Promises with fair service queueing
 *
 * Tasks are organized into N queues (the default queue, plus any others you create).
 * Only one task may run at a time. Each queue has its priority expressed as a *period*;
 * lower period means higher priority. You can add a task to a queue by providing its key, or omit the
 * key for the default queue. The default queue has period = 1.
 *
 * If you add a task and there is no other one running, it starts immediately. Otherwise,
 * you are in a "congestion period", and tasks from each queue run in turn. So that lower priority
 * (higher period) tasks don't get completely starved, each queue gets to run for a fraction of time.
 * If queue A has twice the period of queue B, then A will get half of the time fraction that B does.
 *
 * Since this is a run-to-completion scheduler, the time fractions given to each task during a
 * congestion period can't be exact. Instead, each queue has an integrator, which accumulates time spent
 * on the queue's tasks multiplied by its period. At the end of each task, the queue with the lowest integrator
 * (out of those that have pending tasks) will run. If there are no pending tasks, the integrators
 * reset to zero, and the congestion period ends.
 */
export class PromiseTaskQueue {
    maxIntegrator = Infinity;
    readonly defaultQueue = Symbol();

    private queues = new Map<PromiseTaskQueueKey, PromiseTaskInnerQueue>();
    private busySubject = new BehaviorSubject(false);
    private runningQueue: PromiseTaskInnerQueue | undefined;

    constructor() {
        this.queues.set(this.defaultQueue, { tasks: new Array(), period: 1, integrator: 0 });
    }

    get busy() {
        return this.busySubject.value;
    }

    get busyObs() {
        return this.busySubject.asObservable();
    }

    get queueKeys() {
        return Array.from(this.queues.keys());
    }

    createQueue(key: PromiseTaskQueueKey, period: number) {
        if (this.queues.has(key)) {
            throw new Error(`Queue with key ${String(key)} already exists`);
        }
        this.queues.set(key, { tasks: new Array(), period, integrator: 0 });
    }

    hasQueue(key: PromiseTaskQueueKey) {
        return this.queues.has(key);
    }

    setQueuePeriod(key: PromiseTaskQueueKey, period: number) {
        const queue = this.queues.get(key) || { tasks: new Array(), period, integrator: 0 };
        this.queues.set(key, queue);

        const prevPeriod = queue.period;
        queue.period = period;

        if (isFinite(period)) {
            if (prevPeriod > 0) {
                queue.integrator *= period / prevPeriod;
            } else if (!isFinite(prevPeriod) && !this.busySubject.value) {
                // queue was raised from zero priority, see if it has anything to run
                this.startNext();
            }
        }
    }

    getQueuePeriod(key: PromiseTaskQueueKey) {
        return this.queues.get(key)?.period;
    }

    deleteQueue(key: PromiseTaskQueueKey) {
        const queue = this.queues.get(key);
        if (queue?.tasks.length || this.runningQueue === queue) {
            throw new Error(`Queue ${String(key)} is busy`);
        }
        this.queues.delete(key);
    }

    add<T>(task: () => Promise<T>, key: PromiseTaskQueueKey = this.defaultQueue): Promise<T> {
        return new Promise((resolve, reject) => {
            const queue = this.queues.get(key);
            if (!queue) {
                throw new Error(`No queue with key ${String(key)}`);
            }
            const entry = { task, resolve, reject };

            if (!this.busySubject.value && isFinite(queue.period)) {
                this.busySubject.next(true);
                this.exec(entry, queue);
            } else {
                queue.tasks.push(entry as PromiseTaskQueueEntry<T>);
            }
        });
    }

    clear(key: PromiseTaskQueueKey | undefined = undefined) {
        if (key === undefined) {
            for (const queue of this.queues.values()) {
                queue.tasks.splice(0);
            }
        } else {
            const queue = this.queues.get(key);
            if (!queue) {
                throw new Error(`No queue with key ${String(key)}`);
            }
            queue.tasks.splice(0);
        }
    }

    private finish(start: number, fromQueue: PromiseTaskInnerQueue) {
        if (!this.busySubject.value) {
            throw new Error('Busy flag was false when finish was called!');
        }
        this.runningQueue = undefined;
        const elapsed = monotonicTime() - start;
        if (isFinite(fromQueue.period)) {
            fromQueue.integrator += elapsed * fromQueue.period;
        }

        const minIntegrator = arrayMin(Array.from(this.queues.values()).map((queue) => queue.integrator));
        for (const queue of this.queues.values()) {
            queue.integrator = Math.min(queue.integrator - minIntegrator, this.maxIntegrator);
        }
        this.startNext();
    }

    private startNext() {
        // infinite period means don't run any tasks from the queue
        const runnableQueues = Array.from(this.queues.values()).filter((queue) => queue.tasks.length && isFinite(queue.period));

        // find the queue that has gotten the least (weighted) time in this congestion period
        // if integrators are equal, sort by period
        runnableQueues.sort((a, b) => (a.integrator === b.integrator ? a.period - b.period : a.integrator - b.integrator));

        const nextQueue = runnableQueues[0];

        if (nextQueue) {
            const task = nextQueue.tasks.shift()!;
            this.exec(task, nextQueue);
        } else {
            for (const queue of this.queues.values()) {
                queue.integrator = 0; // congestion period has ended, reset the counters
            }
            this.busySubject.next(false);
        }
    }

    private async exec<T>(entry: PromiseTaskQueueEntry<T>, queue: PromiseTaskInnerQueue) {
        this.runningQueue = queue;
        const start = monotonicTime();
        try {
            const result = await entry.task();
            entry.resolve(result);
            this.finish(start, queue);
        } catch (err) {
            entry.reject(err);
            this.finish(start, queue);
        }
    }
}

export const clampRange = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// index of first array member where lessThan(array[i]) === false, or array.length if none exists
// from C++ STL reference implementation
export const arrayLowerBoundPredicate = <T>(array: Array<T>, lessThan: (T) => boolean): number => {
    let begin = 0;
    let count = array.length;
    while (count > 0) {
        const step = count >> 1;
        const mid = begin + step;
        if (lessThan(array[mid])) {
            begin = mid + 1;
            count -= step + 1;
        } else {
            count = step;
        }
    }
    return begin;
};

export const arrayLowerBound = <T>(array: Array<T>, value: T): number => arrayLowerBoundPredicate(array, (elem: T) => elem < value);

// index of first array member where lessThanOrEqual(array[i]) === false, or array.length if none exists
// from C++ STL reference implementation
export const arrayUpperBoundPredicate = <T>(array: Array<T>, lessThanOrEqual: (a: T) => boolean): number =>
    arrayLowerBoundPredicate(array, lessThanOrEqual); // algorithm is the same in this form!

export const arrayUpperBound = <T>(array: Array<T>, value: T): number => arrayLowerBoundPredicate(array, (elem: T) => elem <= value);

export const safeErrorString = (err: any): string => {
    if (err) {
        if (err.toString) {
            const str = err.toString();
            if (!str.match(/^\[object .*\]$/)) {
                return str;
            }
        }
        for (const key of ['errorMessage', 'message', 'msg']) {
            if (typeof err[key] === 'string') {
                return err[key];
            }
        }
    }
    if (typeof err === 'undefined') {
        return 'undefined';
    }
    if (typeof err === 'object' && Object.keys(err).length) {
        return inspect(err);
    }
    return '';
};

export const compare = <T>(lhs: T, rhs: T): number => {
    if (lhs < rhs) {
        return -1;
    } else if (lhs > rhs) {
        return 1;
    } else {
        return 0;
    }
};

export const compareUndefinedLow = <T>(lhs: T | void, rhs: T | void): number => {
    if (typeof lhs === 'undefined' || lhs === null) {
        if (typeof rhs === 'undefined') {
            return 0;
        } else {
            return -1;
        }
    } else if (typeof rhs === 'undefined' || rhs === null) {
        return 1;
    } else if (lhs < rhs) {
        return -1;
    } else if (lhs > rhs) {
        return 1;
    } else {
        return 0;
    }
};

export const compareUndefinedHigh = <T>(lhs: T | void, rhs: T | void): number => {
    if (typeof lhs === 'undefined' || lhs === null) {
        if (typeof rhs === 'undefined') {
            return 0;
        } else {
            return 1;
        }
    } else if (typeof rhs === 'undefined' || rhs === null) {
        return -1;
    } else if (lhs < rhs) {
        return -1;
    } else if (lhs > rhs) {
        return 1;
    } else {
        return 0;
    }
};

interface ProgParseSegment {
    base: number;
    data: Array<number>;
}

export interface ProgSegment {
    base: number;
    data: ArrayBuffer;
}

export class SRecord {
    private static convertLine(text: string): ProgParseSegment | undefined {
        if (text.length < 4 || text.length % 2 !== 0) {
            throw 'invalid length ' + text.length.toString();
        }
        if (text[0] !== 'S' || !text[1].match(/[0-9]/)) {
            throw 'invalid record type';
        }

        let byteSum = 0;
        const bytes = new Array((text.length - 2) / 2);
        for (let i = 0; i < bytes.length; ++i) {
            const byte = parseInt(text.substr(i * 2 + 2, 2), 16);
            if (byte === undefined || isNaN(byte)) {
                throw 'byte ' + i.toString() + ' failed to convert';
            }
            bytes[i] = byte;
            byteSum += byte;
        }

        if (byteSum % 0x100 !== 0xff) {
            // bad checksum
            throw 'bad checksum';
        }

        bytes.pop(); // remove the checksum byte

        const recordType = parseInt(text[1], 10);
        if (recordType < 1 || recordType > 3) {
            return undefined;
        }

        if (bytes.length !== bytes.shift()) {
            // mismatch with length field
            throw 'bad record length';
        }

        const addrLength = recordType + 1;
        if (bytes.length < addrLength + 1) {
            throw 'truncated line';
        }

        let base = 0;
        for (let i = 0; i < addrLength; ++i) {
            base <<= 8;
            base |= bytes.shift();
        }

        return {
            base,
            data: bytes,
        };
    }

    static parse(text: string): Array<ProgSegment> {
        // eslint-disable-next-line no-control-regex
        const lines = text.replace(/\x0D/g, '').split('\n');
        if (lines[lines.length - 1].length === 0) {
            lines.splice(-1); // don't error on trailing newline
        }

        let segments = new Array<ProgParseSegment>(lines.length);
        let nSegments = 0;
        for (let i = 0; i < lines.length; ++i) {
            try {
                const parsed = SRecord.convertLine(lines[i]);
                if (parsed) {
                    segments[nSegments++] = parsed;
                }
            } catch (err) {
                throw err + ' at line ' + i.toString();
            }
        }
        if (!nSegments) {
            throw 'no program segments found';
        }
        segments = segments.slice(0, nSegments);
        segments.sort((a, b) => (a.base < b.base ? -1 : a.base > b.base ? 1 : 0));

        const mergedSegmentRanges = new Array<[number, number]>();
        for (const seg of segments) {
            const lastRange = mergedSegmentRanges[mergedSegmentRanges.length - 1];
            if (!lastRange) {
                mergedSegmentRanges.push([seg.base, seg.base + seg.data.length]);
            } else {
                const prevTop = lastRange[1];
                if (seg.base < prevTop) {
                    throw 'overlapping program segments';
                } else if (seg.base === prevTop) {
                    lastRange[1] += seg.data.length;
                } else {
                    mergedSegmentRanges.push([seg.base, seg.base + seg.data.length]);
                }
            }
        }

        const mergedSegments = mergedSegmentRanges.map((r) => ({ base: r[0], data: new Array(r[1] - r[0]) }));

        let iMerge = 0;
        for (const seg of segments) {
            let activeMergedSeg = mergedSegments[iMerge];
            if (seg.base > activeMergedSeg.base + activeMergedSeg.data.length) {
                activeMergedSeg = mergedSegments[++iMerge];
            }

            const offset = seg.base - activeMergedSeg.base;
            for (let i = 0; i < seg.data.length; ++i) {
                activeMergedSeg.data[i + offset] = seg.data[i];
            }
        }

        return mergedSegments.map((seg) => ({ base: seg.base, data: new Uint8Array(seg.data).buffer as ArrayBuffer }));
    }
}

export class ValuePromiser<T> {
    private subject: Subject<T> | undefined = new Subject<T>();
    private valueInt: T;
    private error: { value: any };

    notify = (value: T) => {
        this.valueInt = value;
        if (this.subject) {
            this.subject.next(value);
            this.subject.complete();
            this.subject = undefined;
        }
    };

    fail = (err: any) => {
        if (this.subject) {
            this.error = { value: err };
            this.subject.error(err);
            this.subject = undefined;
        }
    };

    get(): Promise<T> {
        if (this.error) {
            return Promise.reject(this.error.value);
        }
        if (this.subject) {
            return firstValueFrom(this.subject);
        }
        return Promise.resolve(this.valueInt);
    }

    get value(): T {
        if (this.subject) {
            throw 'unresolved';
        }
        if (this.error) {
            throw this.error.value;
        }
        return this.valueInt;
    }

    get isNotified() {
        return !this.subject && !this.error;
    }

    get isFailed() {
        return Boolean(this.error);
    }

    get failure() {
        return this.error ? this.error.value : undefined;
    }
}

export class WaitPromiser extends ValuePromiser<void> {}

export const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const concatArrayBuffers = (a: ArrayBufferLike, b: ArrayBufferLike): ArrayBuffer => {
    const c = new Uint8Array(a.byteLength + b.byteLength);
    c.set(new Uint8Array(a), 0);
    c.set(new Uint8Array(b), a.byteLength);
    return c.buffer as ArrayBuffer;
};

export const arrayBuffersEqual = (a: ArrayBufferLike, b: ArrayBufferLike): boolean => {
    if (a.byteLength !== b.byteLength) {
        return false;
    }
    const u8a = new Uint8Array(a);
    const u8b = new Uint8Array(b);
    for (const i of u8a.keys()) {
        if (u8a[i] !== u8b[i]) {
            return false;
        }
    }
    return true;
};

export const turn = async (count = 1) => {
    for (let i = 0; i < count; ++i) {
        await Promise.resolve();
    }
};

export const delayTurn =
    (count = 1) =>
    <T>(source: Observable<T>) =>
        new Observable<T>((observer) =>
            source.subscribe({
                next: (value) => turn(count).then(() => observer.next(value)),
                error: (err) => turn(count).then(() => observer.error(err)),
                complete: () => turn(count).then(() => observer.complete()),
            }),
        );

export const delayToFrame = () =>
    new Observable<void>((subscriber) => {
        let cancelled = false;
        window.requestAnimationFrame(() => {
            if (!cancelled) {
                subscriber.next();
                subscriber.complete();
            }
        });
        return { unsubscribe: () => (cancelled = true) };
    });

// while blocked, subscribers get no new values
// when unblocked, all subscribers get the latest value
// while unblocked, values pass through
// output is BehaviorSubject (hot)
export class ObservableGate<T> {
    private subject = new BehaviorSubject<T | undefined>(undefined);
    private latest: T | undefined = undefined;
    private subscription: Subscription;

    constructor(
        observable: Observable<T>,
        private blocked = false,
    ) {
        this.subscription = observable.subscribe({ next: this.onItem });
    }

    block() {
        this.blocked = true;
    }

    unblock() {
        if (this.blocked) {
            this.blocked = false;
            if (this.subject.value !== this.latest) {
                this.subject.next(this.latest);
            }
        }
    }

    teardown() {
        this.subscription.unsubscribe();
    }

    get obs() {
        return this.subject.asObservable();
    }

    get value() {
        return this.subject.value;
    }

    private onItem = (item: T) => {
        this.latest = item;

        if (!this.blocked) {
            this.subject.next(item);
        }
    };
}

// while blocked, subscribers get no new values
// when unblocked, all subscribers get all the values delivered while blocked, in order
// while unblocked, values pass through
export class ObservableAccumulator<T> {
    private subject = new Subject<T>();
    private buffer = new Array<T>();

    constructor(
        observable: Observable<T>,
        private blocked = false,
    ) {
        observable.subscribe({ next: this.onItem });
    }

    block() {
        this.blocked = true;
    }

    unblock() {
        if (this.blocked) {
            this.blocked = false;
            while (this.buffer.length) {
                this.subject.next(this.buffer.shift()!);
            }
        }
    }

    get obs() {
        return this.subject.asObservable();
    }

    private onItem = (item: T) => {
        if (this.blocked) {
            this.buffer.push(item);
        } else {
            this.subject.next(item);
        }
    };
}

export const asciiVectorToString = (ascii: number[]): string => {
    const zeroIndex = ascii.indexOf(0);
    const trimmed = zeroIndex > 0 ? ascii.slice(0, zeroIndex) : ascii;
    return String.fromCharCode(...trimmed);
};

export const stringToAsciiVector = (str: string): number[] => range(str.length).map((i) => str.charCodeAt(i));

export const appleAppStoreShortName = (fullName: string): string => {
    const phase1 = fullName.replace(/\u00a9|\u2122|\u00ae/g, ''); // strips copyright, trademark, registered trademark
    const phase2 = phase1.replace('&', 'and');
    const phase3 = camelCase(phase2); // strips whitespace and punctuation, transforms accented characters to base
    return phase3.toLowerCase();
};

export const touchToMouseClick = (event: MouseEvent | TouchEvent): MouseEvent => {
    if (event instanceof TouchEvent) {
        // const type = { 'touchstart': 'mousedown', 'touchmove': 'mousemove', 'touchend': 'mouseup' }[event.type] || '';
        const touch = event.changedTouches[0] || event.touches[0];
        const mouseEvent = new MouseEvent('clicked', {
            screenX: touch.screenX,
            screenY: touch.screenY,
            clientX: touch.clientX,
            clientY: touch.clientY,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            metaKey: event.metaKey,
            detail: event.detail,
            view: event.view,
            bubbles: event.bubbles,
            cancelable: event.cancelable,
        });
        event.preventDefault();
        event.target?.dispatchEvent(mouseEvent);
        return mouseEvent;
    } else {
        return event;
    }
};

export const dispatchEventsToResetIonSegmentButtonAfterContextMenu = (element: ElementRef) => {
    // after context menu is closed, dispatch events to reset button state
    // fixes issue on some Macs where the ion-segment-buttons appear active on hover after context menu is closed
    const eventInit: MouseEventInit = {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0,
        buttons: 0,
    };
    element?.nativeElement.dispatchEvent(new PointerEvent('pointerup', eventInit));
    element?.nativeElement.dispatchEvent(new MouseEvent('mouseup', eventInit));
};

/** Generates a string of all the modifier key names, in lowercase, sorted and comma-separated, for easier comparison. */
export const keyboardModifierList = (event: KeyboardEvent) => {
    const mods: string[] = [];
    if (event.altKey) {
        mods.push('alt');
    }
    if (event.ctrlKey) {
        mods.push('ctrl');
    }
    if (event.metaKey) {
        mods.push('meta');
    }
    if (event.shiftKey) {
        mods.push('shift');
    }

    return mods.join(',');
};

/**
 * Takes an iterable of values of any type, but they are only considered as truthy, undefined, or falsy.
 * Returns true if any is truthy, else undefined if any is undefined, else false.
 */
export const someTristate = (iter: Iterable<any>): boolean | undefined => {
    let isUndefined = false;
    for (const elem of iter) {
        if (elem) {
            return true;
        }
        if (elem === undefined) {
            isUndefined = true;
        }
    }
    return isUndefined ? undefined : false;
};

/**
 * Takes an iterable of async functions that may return any type, but they are only considered as truthy, undefined, or falsy.
 * Returns true if any returns truthy, else undefined if any returns undefined, else false.
 */
export const someTristateAsync = async (iter: Iterable<() => Promise<any>>): Promise<boolean | undefined> => {
    let isUndefined = false;
    for (const func of iter) {
        const elem = await func();
        if (elem) {
            return true;
        }
        if (elem === undefined) {
            isUndefined = true;
        }
    }
    return isUndefined ? undefined : false;
};

/**
 * Takes an iterable of values of any type, but they are only considered as truthy, undefined, or falsy.
 * Returns true if all are truthy, else undefined if any is undefined, else false.
 */
export const everyTristate = (iter: Iterable<any>): boolean | undefined => {
    let isUndefined = false;
    for (const elem of iter) {
        if (elem === false) {
            return false;
        }
        if (elem === undefined) {
            isUndefined = true;
        }
    }
    return isUndefined ? undefined : true;
};

/**
 * Takes an iterable of async functions that may return any type, but they are only considered as truthy, undefined, or falsy.
 * Returns true if all return truthy, else undefined if any returns undefined, else false.
 */
export const everyTristateAsync = async (iter: Iterable<() => Promise<any>>): Promise<boolean | undefined> => {
    let isUndefined = false;
    for (const func of iter) {
        const elem = await func();
        if (elem === false) {
            return false;
        }
        if (elem === undefined) {
            isUndefined = true;
        }
    }
    return isUndefined ? undefined : true;
};

export const makeStrictIncr = (value: number[], rawStep: number, fixedEnds: boolean, origValue?: number[]): number[] => {
    const out = Array.from(value);
    const startIndex = fixedEnds ? 1 : 0;
    const endIndex = fixedEnds ? value.length - 1 : value.length;
    for (let i = 1; i < endIndex; ++i) {
        if (!origValue || out[i] < origValue[i]) {
            out[i] = Math.max(out[i], out[i - 1] + rawStep);
        }
    }
    for (let i = endIndex - 2; i >= startIndex; --i) {
        if (!origValue || out[i] > origValue[i]) {
            out[i] = Math.min(out[i], out[i + 1] - rawStep);
        }
    }
    return out;
};

export interface LodashThrottleOptions extends ThrottleSettings {
    emitOnError?: boolean; // defaults to false
    emitOnComplete?: boolean; // defaults to false
}

export const lodashThrottle =
    (wait: number, options?: LodashThrottleOptions) =>
    <T>(source: Observable<T>) =>
        new Observable<T>((observer) => {
            let deferredValue: { value: T } | undefined;
            const throttledNext = throttle(
                (value) => {
                    deferredValue = undefined;
                    observer.next(value);
                },
                wait,
                options,
            );
            let subscription: Subscription | undefined;

            subscription = source.subscribe({
                next: (value: T) => {
                    if (options?.emitOnComplete || options?.emitOnError) {
                        deferredValue = { value };
                    }
                    throttledNext(value);
                },
                error: (err: any) => {
                    subscription?.unsubscribe();
                    throttledNext.cancel();
                    if (options?.emitOnError && deferredValue) {
                        observer.next(deferredValue.value);
                    }
                    observer.error(err);
                },
                complete: () => {
                    subscription?.unsubscribe();
                    throttledNext.cancel();
                    if (options?.emitOnComplete && deferredValue) {
                        observer.next(deferredValue.value);
                    }
                    observer.complete();
                },
            });
        });

export const waitFor =
    <T>(value: T, timeoutValue: number) =>
    (source: Observable<T>) =>
        source.pipe(
            filter((v) => v === value),
            timeout({ each: timeoutValue }),
            first(),
            map(() => undefined),
        );

export const onZone =
    (zone: { run: (fn: () => any) => any }) =>
    <T>(source: Observable<T>) =>
        new Observable<T>((observer) => {
            const subscription = source.subscribe({
                next: (value: T) => {
                    zone.run(() => {
                        observer.next(value);
                    });
                },
                error: (err: any) => {
                    zone.run(() => {
                        observer.error(err);
                    });
                },
                complete: () => {
                    zone.run(() => {
                        observer.complete();
                    });
                },
            });
            // Return teardown function for proper RxJS 7 cleanup
            return () => subscription.unsubscribe();
        });

export const bufferUntilSubscribe =
    () =>
    <T>(source: Observable<T>) => {
        // the body of this function (which is being returned from bufferUntilSubscribe()) gets run immediately by pipe()
        let buffer = new Array<T>();
        let observerOnceSubscribed: Observer<T>;
        let subscription: Subscription | undefined;

        subscription = source.subscribe({
            next: (value: T) => {
                if (observerOnceSubscribed) {
                    observerOnceSubscribed.next(value);
                } else {
                    buffer.push(value);
                }
            },
            error: (err: any) => {
                subscription?.unsubscribe();
                if (observerOnceSubscribed) {
                    observerOnceSubscribed.error(err);
                }
            },
            complete: () => {
                subscription?.unsubscribe();
                if (observerOnceSubscribed) {
                    observerOnceSubscribed.complete();
                }
            },
        });
        return defer(
            () =>
                new Observable<T>((observer) => {
                    // this runs on subscription
                    observerOnceSubscribed = observer;
                    // shift out the saved items, then delete them
                    for (const elem of buffer) {
                        observer.next(elem);
                    }
                    buffer = [];
                }),
        );
    };

// Delays `next`, but not `error` or `complete`. Any `next`s within `msec` of `error`/`complete` are dropped.
export const delayValues =
    (msec: number) =>
    <T>(source: Observable<T>) =>
        new Observable<T>((observer) => {
            let subscription: Subscription | undefined;
            let closed = false;

            subscription = source.subscribe({
                next: async (value: T) => {
                    await delay(msec);
                    if (!closed) {
                        observer.next(value);
                    }
                },
                error: (err: any) => {
                    subscription?.unsubscribe();
                    closed = true;
                    observer.error(err);
                },
                complete: () => {
                    subscription?.unsubscribe();
                    closed = true;
                    observer.complete();
                },
            });
        });

export interface ConstructorType<T> {
    new (...args: any[]): T;
}

export const resolveTimeout = <T>(promise: Promise<T>, msec: number, exc: any = 'timeout'): Promise<T> => {
    if (!msec) {
        return promise;
    }
    return new Promise((res, rej) => {
        let resolved = false;
        let rejected = false;
        const handle = setTimeout(() => {
            if (!resolved) {
                rejected = true;
                rej(exc);
            }
        }, msec);
        promise
            .then((ret) => {
                if (!rejected) {
                    resolved = true;
                    res(ret);
                    clearTimeout(handle);
                }
            })
            .catch((err) => {
                if (!rejected) {
                    rejected = true;
                    rej(err);
                    clearTimeout(handle);
                }
            });
    });
};

export const monotonicTime = () => {
    if (performance) {
        return performance.now();
    }
    if (typeof process !== 'undefined' && !!process?.hrtime) {
        const [sec, nsec] = process.hrtime();
        return sec * 1000 + nsec / 1000000;
    }
    return Date.now();
};

export const monotonicTimeStr = () => monotonicTime().toFixed(3);

export const isValidNumber = (x: any) => typeof x === 'number' && isFinite(x);

export interface OxfordJoinParams {
    conjunction?: string;
    singularPrefix?: string;
    pluralPrefix?: string;
    singularSuffix?: string;
    pluralSuffix?: string;
}

export const oxfordJoin = (arr: string[], params?: OxfordJoinParams) => {
    let conjunction = 'and';
    let singularPrefix = '';
    let pluralPrefix = '';
    let singularSuffix = '';
    let pluralSuffix = '';
    if (params) {
        conjunction = params.conjunction || conjunction;
        singularPrefix = params.singularPrefix || singularPrefix;
        pluralPrefix = params.pluralPrefix || pluralPrefix;
        singularSuffix = params.singularSuffix || singularSuffix;
        pluralSuffix = params.pluralSuffix || pluralSuffix;
    }
    if (arr.length === 0) {
        return '';
    }
    if (arr.length === 1) {
        return `${singularPrefix}${arr[0]}${singularSuffix}`;
    }
    if (arr.length === 2) {
        return `${pluralPrefix}${arr[0]} ${conjunction} ${arr[1]}${pluralSuffix}`;
    }
    return `${pluralPrefix}${arr.slice(0, arr.length - 1).join(', ')}, ${conjunction} ${arr[arr.length - 1]}${pluralSuffix}`;
};

export interface DeltaToHmssConfig {
    hideZeroHours?: boolean;
    secondsDigits?: number;
}

export const deltaToHmss = (msec: number, config: DeltaToHmssConfig) => {
    if (isNaN(msec)) {
        return '';
    }

    const { hideZeroHours, secondsDigits } = { hideZeroHours: false, secondsDigits: 3, ...config };

    const roundMsec = Math.round(msec);
    const hours = Math.floor(roundMsec / 3600000);
    const minutes = Math.floor((roundMsec % 3600000) / 60000);
    const seconds = (roundMsec % 60000) / 1000;

    const elems = [padStart(seconds.toFixed(secondsDigits), 3 + secondsDigits, '0')];
    if (hours || !hideZeroHours) {
        elems.unshift(padStart(minutes.toFixed(0), 2, '0'));
        elems.unshift(hours.toFixed(0));
    } else {
        elems.unshift(minutes.toFixed(0));
    }
    return elems.join(':');
};

export const filenameSafeDate = (stamp: Date) =>
    [
        stamp.getFullYear().toString(),
        padStart((stamp.getMonth() + 1).toString(), 2, '0'),
        padStart(stamp.getDate().toString(), 2, '0'),
    ].join('-');

export const filenameSafeTime = (stamp: Date) =>
    [
        padStart(stamp.getHours().toString(), 2, '0'),
        padStart(stamp.getMinutes().toString(), 2, '0'),
        padStart(stamp.getSeconds().toString(), 2, '0'),
    ].join('-');

type SIACreator<T, U> = (source: U) => T;
type SIAComparator<T, U> = (existing: T, source: U) => boolean | void;
type SIAUpdater<T, U> = (existing: T, source: U) => boolean | void;

interface MonoTypeStableIdentityArrayConfig<T> {
    create?: SIACreator<T, T>;
    compare: SIAComparator<T, T>; // returns true if update or replacement is needed
    update?: SIAUpdater<T, T>; // returns true if replacement is needed
}
interface ConvertingStableIdentityArrayConfig<T, U> {
    create: SIACreator<T, U>;
    compare: SIAComparator<T, U>; // returns true if update or replacement is needed
    update: SIAUpdater<T, U>; // returns true if replacement is needed
}

export const isNotEqual = <T>(a: T, b: T) => !isEqual(a, b);

export type StableIdentityArrayConfig<T, U> = T extends U
    ? U extends T
        ? MonoTypeStableIdentityArrayConfig<T>
        : ConvertingStableIdentityArrayConfig<T, U>
    : ConvertingStableIdentityArrayConfig<T, U>;

const defaultSiaUpdater = <T extends Record<any, any>>(e: T, s: T) => {
    for (const key of Object.keys(e)) {
        if (!(key in s)) {
            delete e[key];
        }
    }
    Object.assign(e, s);
    return false;
};

export class StableIdentityArray<T extends Record<any, any>, U = T> {
    readonly cacheSignal: Signal<T[]>;

    private cache: T[] = [];
    private create: SIACreator<T, U>;
    private compare: SIAComparator<T, U>;
    private update: SIAUpdater<T, U>;
    // Always-fires signal — consumers rely on it to re-run on every `set()`, even though
    // the underlying array reference is stable (items are mutated in place to preserve
    // per-element identity for `@for` tracking).
    private readonly _cacheSignal = signal<T[]>(this.cache, { equal: () => false });

    constructor(config: StableIdentityArrayConfig<T, U>) {
        this.create = config.create || (cloneDeep as SIACreator<T, U>);
        this.compare = config.compare;
        this.update = config.update || defaultSiaUpdater;
        this.cacheSignal = this._cacheSignal.asReadonly();
    }

    get(): T[] {
        return this.cache;
    }

    set(v: U[]) {
        const prevLength = this.cache.length;
        this.cache.length = v.length;
        for (let i = 0; i < v.length; ++i) {
            if (i >= prevLength || (this.compare(this.cache[i], v[i]) && this.update(this.cache[i], v[i]))) {
                this.cache[i] = this.create(v[i]);
            }
        }
        this._cacheSignal.set(this.cache);
    }
}

export const nonNullOnly = <T>() => filter((v) => v !== undefined && v !== null) as OperatorFunction<T, NonNullable<T>>;
export const truthyOnly = <T>() => filter((v) => !!v) as OperatorFunction<T, Exclude<T, false | 0 | -0 | 0n | '' | undefined | null>>;
export const falsyOnly = <T>() =>
    filter((v) => !v) as OperatorFunction<
        T,
        Exclude<T, true | string | number | bigint | object | ((...args: any[]) => any) | symbol> | '' | 0 | 0n
    >;
export const filterTruthy = <T>(v: Array<T>): Array<NonNullable<T>> => v.filter((e) => e) as Array<NonNullable<T>>;

const rgbRegex = /^ *rgb *\( *(\d+, *\d+, *\d+ *) *\) *$/i;
const rgbaRegex = /^ *rgba *\( *(\d+, *\d+, *\d+, *(?:\d\.)\d* *) *\) *$/i;
export const colorToRgba = (color: string) => {
    if (color.startsWith('#')) {
        let r = 0;
        let g = 0;
        let b = 0;
        let a = 1;
        const hex = color.substring(1);
        switch (hex.length) {
            case 8:
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
                a = parseInt(hex.substring(6, 8), 16) / 255;
                break;
            case 6:
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
                break;
            case 4:
                r = parseInt(hex.substring(0, 1), 16) * 0x11;
                g = parseInt(hex.substring(1, 2), 16) * 0x11;
                b = parseInt(hex.substring(2, 3), 16) * 0x11;
                a = parseInt(hex.substring(3, 4), 16) / 15;
                break;
            case 3:
                r = parseInt(hex.substring(0, 1), 16) * 0x11;
                g = parseInt(hex.substring(1, 2), 16) * 0x11;
                b = parseInt(hex.substring(2, 3), 16) * 0x11;
                break;
        }
        return `${r},${g},${b},${a}`;
    } else if (color.match(rgbRegex)) {
        return color.replace(rgbRegex, '$1,1').replace(/ /g, '');
    } else if (color.match(rgbaRegex)) {
        return color.replace(rgbaRegex, '$1').replace(/ /g, '');
    }
    throw new Error('Malformed input ' + color);
};

export const colorToRgb = (color: string) => colorToRgba(color).split(',').slice(0, 3).join(',');

export type ThenArg<T> = T extends PromiseLike<infer U> ? U : T;

export { DeepReadonly } from '../param/util/param-system-def-types';

export type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
export type DistributivePick<T, K extends keyof T> = T extends unknown ? Pick<T, K> : never;

export const basicStats = (numbers: number[]) => {
    if (!numbers.length) {
        return { min: NaN, max: NaN, mean: NaN, sd: NaN };
    }
    const min = arrayMin(numbers);
    const max = arrayMax(numbers);
    const mean = numbers.reduce((accum, value) => accum + value, 0) / numbers.length;
    const sumOfDiffSquares = numbers.reduce((accum, value) => accum + Math.pow(value - mean, 2), 0);
    const sd = Math.sqrt(sumOfDiffSquares / (numbers.length - 1));
    return { min, max, mean, sd };
};

export const numberNearlyEqual = (a: number, b: number) => {
    if (a === b) {
        return true;
    }
    const delta = Math.abs(a - b);
    if (a === 0 || b === 0 || delta < Number.MIN_VALUE) {
        return delta < Number.EPSILON * Number.MIN_VALUE;
    }
    const totalAbs = Math.abs(a) + Math.abs(b);
    return delta / Math.min(totalAbs, Number.MAX_VALUE) < Number.EPSILON;
};

export const engrValueEqual = (a: string | number | number[], b: string | number | number[]) => {
    if (typeof a === 'string' && typeof b === 'string') {
        return a === b;
    } else if (typeof a === 'number' && typeof b === 'number') {
        return numberNearlyEqual(a, b);
    } else if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
        return range(a.length).every((i) => numberNearlyEqual(a[i], b[i]));
    } else {
        return false;
    }
};

export const interp1d = (arr: { x: number; y: number }[], xi: number) => {
    if (xi < arr[0].x || xi > arr[arr.length - 1].x) {
        throw new Error(`Interpolant (${xi}) is outside array bounds [${arr[0].x}, ${arr[arr.length - 1].x}]`);
    }

    const i1 = arr.findIndex((e) => e.x > xi);
    if (i1 < 0) {
        return arr[arr.length - 1].y;
    }
    if (i1 === 0) {
        return arr[0].y; //
    }
    const p0 = arr[i1 - 1];
    const p1 = arr[i1];
    const deltaY = p1.y - p0.y;
    const deltaX = p1.x - p0.x;
    if (deltaY === 0 || deltaX === 0) {
        return p0.y;
    }
    return p0.y + (deltaY * (xi - p0.x)) / deltaX;
};

export const mapsEqual = <K, V>(map1: Map<K, V>, map2: Map<K, V>) => {
    if (map1.size !== map2.size) {
        return false;
    }
    for (const [k, v] of map1) {
        if (!map2.has(k) || map2.get(k) !== v) {
            return false;
        }
    }
    return true;
};
